import { grnEn, grnTr, orderErrorsEn, orderErrorsTr, poEn, poTr } from './orders.js';
import { awardEn, awardErrorsEn, awardErrorsTr, awardTr } from './purchasing-award.js';

/**
 * Satın alma ekranlarının metinleri (M12b; ADR 0026). Ekran adları tasarımdaki gibi İngilizce
 * (`satin-alma-akisi.jpeg`, `gemi-satin-alma.jpeg`); Türkçe ikinci dil.
 */

const errorsEn = {
  GENERIC: 'The operation failed. Try again.',
  NETWORK: 'No connection. Check the network and try again.',
  FORBIDDEN: 'You are not allowed to do this.',
  NOT_FOUND: 'The record was not found. It may have been removed.',
  VALIDATION_FAILED: 'Some fields are invalid.',
  ROW_VERSION_CONFLICT: 'Someone else changed this requisition. Reopen it and try again.',
  INVALID_CREDENTIALS: 'The password is wrong.',
  INVALID_PASSWORD: 'The password is wrong.',
  TOO_MANY_ATTEMPTS: 'Too many wrong passwords. Wait 15 minutes and try again.',
  CONTENT_CHANGED: 'The requisition changed meanwhile. Reopen it and try again.',
  ALREADY_SUBMITTED: 'The requisition is already submitted.',
  NOT_PENDING: 'The requisition is not waiting for approval.',
  NOT_APPROVER: 'Your role does not approve this step.',
  SELF_APPROVAL_FORBIDDEN: 'You cannot approve your own requisition.',
  RETURN_REASON_REQUIRED: 'Enter the reason for returning.',
  OFFICE_STEP: 'This step is approved in the office.',
  NOT_OFFICE_STEP: 'This step is approved on board.',
  REQUISITION_LOCKED: 'A submitted or approved requisition cannot be changed.',
  REQUISITION_CANCELLED: 'The requisition is cancelled.',
  CANCEL_REASON_REQUIRED: 'Enter the reason for cancelling.',
  NO_LINES: 'Add at least one item.',
  REQUIRED_DATE_PAST: 'The required date is in the past.',
  UNIT_MISMATCH: 'The unit must match the catalogue.',
  UNKNOWN_MATERIAL: 'The material is no longer in the catalogue.',
  REQUESTER_NOT_ON_VESSEL: 'The requester is not on board.',
  APPROVAL_RULE_MISSING: 'The approval matrix has no rule for this amount. Ask the office.',
  INVALID_REQUISITION: 'Some fields are invalid.',
  INVALID_REQUISITION_LINE: 'Some items are invalid.',
  SHIP_IS_WRITER: 'Requisitions are written on board.',
  REQUISITION_NOT_APPROVED: 'The requisition is not approved yet.',
  RFQ_EXISTS: 'This requisition already has an open RFQ.',
  // Ofisin talep revizyonu (ADR 0111 §A)
  REQUISITION_HAS_RFQ:
    'An RFQ was created from this list. Cancel the RFQ before revising the requisition.',
  REVISION_UNCHANGED: 'Nothing changed: the list is the same as the current approved list.',
  REVISION_LINES_MISMATCH: 'The requisition changed meanwhile. Reopen it and try again.',
  REVISION_NO_MISMATCH: 'Someone else revised this requisition. Reopen it and try again.',
  STALE_REQUISITION: 'The requisition changed meanwhile. Reopen it and try again.',
  MATERIAL_ALREADY_LISTED: 'The item is already on the list; change its quantity instead.',
  NO_APPROVED_LINES: 'At least one item must stay on the list.',
  REVISION_REASON_REQUIRED: 'Enter the reason for the changes.',
  SUPPLIER_NOT_APPROVED: 'Only approved and active suppliers can be invited.',
  SUPPLIER_NOT_INVITED: 'The supplier is not invited to this RFQ.',
  DUPLICATE_QUOTATION: 'This supplier already has a quotation.',
  RFQ_CLOSED: 'The RFQ is closed.',
  NO_SUPPLIERS: 'Invite at least one supplier.',
  INCOMPLETE_QUOTATION: 'The quotation does not price every item.',
  QUOTATION_CURRENCY: 'Only quotations in the RFQ currency can be selected.',
  DUPLICATE_SUPPLIER: 'A supplier with the same code already exists.',
  INVALID_SUPPLIER: 'Some fields are invalid.',
  RFQ_ALREADY_SENT: 'The RFQ is already marked as sent.',
  RFQ_ALREADY_PUBLISHED: 'The RFQ is already published to the supplier portal.',
  CLOSING_IN_PAST: 'The deadline must be in the future.',
  INVALID_PUBLICATION: 'Choose who can quote and a valid deadline.',
  PORTAL_QUOTATION:
    'This quotation was submitted by the supplier on the portal. Only the supplier can revise it.',
  RFQ_CLOSED_FOR_QUOTES: 'The deadline has passed; the portal no longer accepts quotations.',
  SUPPLIER_ALREADY_INVITED: 'The supplier is already invited.',
  SELECTION_REASON_REQUIRED: 'This is not the best price. Enter the reason for selection.',
  UNKNOWN_RFQ_LINE: 'The RFQ items changed. Reopen the RFQ and try again.',
  UNKNOWN_REQUISITION_LINE: 'The requisition items changed. Reopen it and try again.',
  INVALID_RFQ: 'Some RFQ fields are invalid.',
  INVALID_QUOTATION: 'Some quotation fields are invalid.',
  RFQ_FIXED: 'The RFQ cannot be moved to another requisition.',
  SAME_APPROVER_FORBIDDEN: 'You approved an earlier step. Another person approves this step.',
  STALE_DECISION: 'The pending step changed meanwhile. Reopen the requisition and try again.',
  RULE_INPUTS_MISSING: 'The submission has not fully arrived from the vessel yet. Try again later.',
  NO_ELIGIBLE_APPROVER:
    'No one on board can approve this requisition. Ask the office to check the crew assignments or the approval matrix.',
  CATALOG_NAME_FIXED: 'The name of a catalogue item cannot be changed.',
  CATALOG_NAME_MISMATCH: 'The name of a catalogue item comes from the catalogue.',
  FUTURE_DATE: 'The date cannot be in the future.',
  // Açık pazar ve bölge (ADR 0113)
  NO_INVITED_SUPPLIERS: 'Invite at least one supplier.',
  NOT_PORTAL_SUPPLIER: 'Only suppliers registered on the portal can be added this way.',
  PORTAL_NOT_PROSPECT: 'This supplier has no pending portal registration.',
  PORTAL_PROSPECT:
    'This supplier registered on the portal by itself. Use "Add to suppliers" instead of an invitation.',
  ...orderErrorsEn,
  // Ödül (ADR 0111 §D)
  ...awardErrorsEn,
};

const errorsTr: typeof errorsEn = {
  GENERIC: 'İşlem yapılamadı. Yeniden deneyin.',
  NETWORK: 'Bağlantı yok. Ağı denetleyip yeniden deneyin.',
  FORBIDDEN: 'Bu işlem için yetkiniz yok.',
  NOT_FOUND: 'Kayıt bulunamadı. Kaldırılmış olabilir.',
  VALIDATION_FAILED: 'Bazı alanlar geçersiz.',
  ROW_VERSION_CONFLICT: 'Talebi başka biri değiştirdi. Yeniden açıp deneyin.',
  INVALID_CREDENTIALS: 'Parola yanlış.',
  INVALID_PASSWORD: 'Parola yanlış.',
  TOO_MANY_ATTEMPTS: 'Çok fazla yanlış parola. 15 dakika bekleyip yeniden deneyin.',
  CONTENT_CHANGED: 'Talep bu arada değişti. Yeniden açıp deneyin.',
  ALREADY_SUBMITTED: 'Talep zaten gönderildi.',
  NOT_PENDING: 'Talep onay beklemiyor.',
  NOT_APPROVER: 'Rolünüz bu adımı onaylamaz.',
  SELF_APPROVAL_FORBIDDEN: 'Kendi talebinizi onaylayamazsınız.',
  RETURN_REASON_REQUIRED: 'İade gerekçesini yazın.',
  OFFICE_STEP: 'Bu adım ofiste onaylanır.',
  NOT_OFFICE_STEP: 'Bu adım gemide onaylanır.',
  REQUISITION_LOCKED: 'Gönderilmiş ya da onaylanmış talep değiştirilemez.',
  REQUISITION_CANCELLED: 'Talep iptal edildi.',
  CANCEL_REASON_REQUIRED: 'İptal gerekçesini yazın.',
  NO_LINES: 'En az bir kalem ekleyin.',
  REQUIRED_DATE_PAST: 'İstenen tarih geçmişte.',
  UNIT_MISMATCH: 'Birim katalogdakiyle aynı olmalı.',
  UNKNOWN_MATERIAL: 'Malzeme artık katalogda yok.',
  REQUESTER_NOT_ON_VESSEL: 'Talep eden gemide görevli değil.',
  APPROVAL_RULE_MISSING: 'Onay matrisinde bu tutar için kural yok. Ofise danışın.',
  INVALID_REQUISITION: 'Bazı alanlar geçersiz.',
  INVALID_REQUISITION_LINE: 'Bazı kalemler geçersiz.',
  SHIP_IS_WRITER: 'Talep gemide yazılır.',
  REQUISITION_NOT_APPROVED: 'Talep henüz onaylanmadı.',
  RFQ_EXISTS: 'Bu talebin açık bir teklif isteği var.',
  REQUISITION_HAS_RFQ:
    'Bu listeden teklif isteği açıldı. Talebi düzenlemeden önce teklif isteğini iptal edin.',
  REVISION_UNCHANGED: 'Değişiklik yok: liste geçerli onaylı listeyle aynı.',
  REVISION_LINES_MISMATCH: 'Talep bu arada değişti. Yeniden açıp tekrar deneyin.',
  REVISION_NO_MISMATCH: 'Talebi bu arada başka biri düzenledi. Yeniden açıp tekrar deneyin.',
  STALE_REQUISITION: 'Talep bu arada değişti. Yeniden açıp tekrar deneyin.',
  MATERIAL_ALREADY_LISTED: 'Kalem listede zaten var; onun miktarını değiştirin.',
  NO_APPROVED_LINES: 'Listede en az bir kalem kalmalı.',
  REVISION_REASON_REQUIRED: 'Değişikliklerin gerekçesini girin.',
  SUPPLIER_NOT_APPROVED: 'Yalnız onaylı ve etkin tedarikçi davet edilir.',
  SUPPLIER_NOT_INVITED: 'Tedarikçi bu teklif isteğine davetli değil.',
  DUPLICATE_QUOTATION: 'Bu tedarikçinin teklifi zaten var.',
  RFQ_CLOSED: 'Teklif isteği kapandı.',
  NO_SUPPLIERS: 'En az bir tedarikçi davet edin.',
  INCOMPLETE_QUOTATION: 'Teklif her kalemi fiyatlamıyor.',
  QUOTATION_CURRENCY: 'Yalnız teklif isteğinin para birimindeki teklif seçilir.',
  DUPLICATE_SUPPLIER: 'Aynı kodla bir tedarikçi zaten var.',
  INVALID_SUPPLIER: 'Bazı alanlar geçersiz.',
  RFQ_ALREADY_SENT: 'Teklif isteği zaten gönderildi olarak işaretli.',
  RFQ_ALREADY_PUBLISHED: 'Teklif isteği tedarikçi portalında zaten yayımlandı.',
  CLOSING_IN_PAST: 'Son tarih gelecekte olmalı.',
  INVALID_PUBLICATION: 'Kimlerin teklif vereceğini ve geçerli bir son tarih seçin.',
  PORTAL_QUOTATION:
    'Bu teklifi tedarikçi portaldan gönderdi. Yalnız tedarikçi yeni sürümle düzeltebilir.',
  RFQ_CLOSED_FOR_QUOTES: 'Son tarih geçti; portal artık teklif almıyor.',
  SUPPLIER_ALREADY_INVITED: 'Tedarikçi zaten davetli.',
  SELECTION_REASON_REQUIRED: 'Bu en iyi fiyat değil. Seçim gerekçesini yazın.',
  UNKNOWN_RFQ_LINE: 'Teklif isteğinin kalemleri değişti. Yeniden açıp deneyin.',
  UNKNOWN_REQUISITION_LINE: 'Talebin kalemleri değişti. Yeniden açıp deneyin.',
  INVALID_RFQ: 'Teklif isteğinin bazı alanları geçersiz.',
  INVALID_QUOTATION: 'Teklifin bazı alanları geçersiz.',
  RFQ_FIXED: 'Teklif isteği başka bir talebe taşınamaz.',
  SAME_APPROVER_FORBIDDEN: 'Önceki adımı siz onayladınız. Bu adımı başka biri onaylar.',
  STALE_DECISION: 'Bekleyen adım bu arada değişti. Talebi yeniden açıp deneyin.',
  RULE_INPUTS_MISSING: 'Gönderim gemiden henüz tam gelmedi. Biraz sonra yeniden deneyin.',
  NO_ELIGIBLE_APPROVER:
    'Gemide bu talebi onaylayabilecek kimse yok. Ofisten atamaları ya da onay matrisini denetlemesini isteyin.',
  CATALOG_NAME_FIXED: 'Katalog kaleminin adı değiştirilemez.',
  CATALOG_NAME_MISMATCH: 'Katalog kaleminin adı katalogdan gelir.',
  FUTURE_DATE: 'Tarih gelecekte olamaz.',
  // Açık pazar ve bölge (ADR 0113)
  NO_INVITED_SUPPLIERS: 'En az bir tedarikçi davet edin.',
  NOT_PORTAL_SUPPLIER: 'Bu yoldan yalnız portala kendi kaydolan tedarikçi eklenir.',
  PORTAL_NOT_PROSPECT: 'Bu tedarikçinin bekleyen portal kaydı yok.',
  PORTAL_PROSPECT:
    'Bu tedarikçi portala kendi kaydoldu. Davet yerine "Tedarikçilere ekle"yi kullanın.',
  ...orderErrorsTr,
  ...awardErrorsTr,
};

/** Alan hataları (form): şema kodları. */
const fieldEn = {
  REQUIRED: 'Required.',
  INVALID_OPTION: 'Choose an option.',
  INVALID_DATE: 'Enter a valid date.',
  INVALID_NUMBER: 'Enter a number.',
  OUT_OF_RANGE: 'Out of range.',
  TOO_LONG: 'Too long.',
  INVALID_CURRENCY: 'Use a three-letter currency code.',
  NO_LINES: 'Add at least one item.',
  REQUIRED_DATE_PAST: 'The required date is in the past.',
  INVALID_EMAIL: 'Enter a valid e-mail address.',
  NO_SUPPLIERS: 'Invite at least one supplier.',
  TOO_MANY_SUPPLIERS: 'Invite at most 20 suppliers.',
  EXCEEDS_ITEMS: 'The discount cannot exceed the items total.',
  OVER_OPEN: 'More than the open quantity.',
  CONDITION_MISMATCH: 'The condition does not match the quantities.',
};
const fieldTr: typeof fieldEn = {
  REQUIRED: 'Zorunlu.',
  INVALID_OPTION: 'Bir seçenek seçin.',
  INVALID_DATE: 'Geçerli bir tarih girin.',
  INVALID_NUMBER: 'Bir sayı girin.',
  OUT_OF_RANGE: 'Aralık dışında.',
  TOO_LONG: 'Çok uzun.',
  INVALID_CURRENCY: 'Üç harfli para birimi kodu girin.',
  NO_LINES: 'En az bir kalem ekleyin.',
  REQUIRED_DATE_PAST: 'İstenen tarih geçmişte.',
  INVALID_EMAIL: 'Geçerli bir e-posta adresi girin.',
  NO_SUPPLIERS: 'En az bir tedarikçi davet edin.',
  TOO_MANY_SUPPLIERS: 'En çok 20 tedarikçi davet edin.',
  EXCEEDS_ITEMS: 'İndirim kalemlerin toplamını aşamaz.',
  OVER_OPEN: 'Açık miktardan fazla.',
  CONDITION_MISMATCH: 'Kalite sonucu miktarlarla uyuşmuyor.',
};

export const purchasingEn = {
  table: { sortBy: 'Sort by {{column}}', actions: 'Actions' },
  crumb: 'Purchasing',
  statuses: {
    draft: 'Draft',
    pending_approval: 'Pending Approval',
    office_approval: 'Awaiting Office Approval',
    approved: 'Approved',
    returned: 'Returned',
    cancelled: 'Cancelled',
    in_progress: 'In Progress',
    po_issued: 'PO Issued',
    closed: 'Closed',
  },
  departments: {
    Deck: 'Deck',
    Engine: 'Engine',
    Electrical: 'Electrical',
    Navigation: 'Navigation',
    Galley: 'Galley',
    Cabin: 'Cabin',
    Safety: 'Safety',
    Medical: 'Medical',
    Provisions: 'Provisions',
    Other: 'Other',
  },
  priorities: { Normal: 'Normal', High: 'High', Urgent: 'Urgent' },
  reasons: {
    Maintenance: 'Maintenance',
    Safety: 'Safety',
    Navigation: 'Navigation',
    Deck: 'Deck',
    Engine: 'Engine',
    Hotel: 'Hotel',
    Galley: 'Galley',
    Crew: 'Crew',
    Provision: 'Provision',
    'Spare Parts': 'Spare Parts',
    Survey: 'Survey',
    Audit: 'Audit',
    Deficiency: 'Deficiency',
    'Planned Maintenance': 'Planned Maintenance',
    Emergency: 'Emergency',
  },
  // Zil (ADR 0091): kararımı bekleyen talepler
  bell: {
    title: 'Purchasing decisions',
    requisition: 'Requisition waiting for your approval',
    escalated: 'submitted by the approver',
    // Ofisten inen onaylı liste (ADR 0111 §A)
    approvedList: 'Approved list received from the office',
    revision: 'revision {{no}}',
  },
  list: {
    title: 'Material Requisition',
    descriptionShip: 'Requisitions of the vessel with their approval status.',
    descriptionWeb: 'Requisitions from the fleet; office approval steps are decided here.',
    new: 'New Requisition',
    vessel: 'Vessel',
    allVessels: 'All Vessels',
    status: 'Status',
    allStatus: 'All Status',
    dateRange: 'Date Range',
    dateRangePlaceholder: 'Request date',
    dateFrom: 'Request date from',
    dateTo: 'Request date to',
    filters: 'Requisition filters',
    table: 'Requisitions',
    columns: {
      no: '#',
      requisitionNo: 'Requisition No',
      vessel: 'Vessel',
      department: 'Department',
      requestedBy: 'Requested By',
      requiredDate: 'Required Date',
      status: 'Status',
      total: 'Total ({{currency}})',
    },
    open: 'Open {{no}}',
    awaitingShip: '{{role}} · on board',
    awaitingOffice: '{{role}} · office',
    showing: 'Showing {{from}}-{{to}} of {{total}}',
    pagination: 'Requisition pages',
    emptyTitle: 'No requisitions yet.',
    emptyShip: 'Open a new requisition for the items the vessel needs.',
    emptyWeb: 'Requisitions are opened on board and appear here after synchronisation.',
    noMatch: 'No requisition matches the filters.',
    error: 'The requisitions could not be loaded.',
    retry: 'Retry',
    unknownTotal: 'Not priced',
    // Stok mevcudu ve ofisin incelemesi (ADR 0111 §A)
    inStock: 'In Stock',
    inStockLines: '{{count}} of {{total}} items',
    review: 'Review',
    allReview: 'All',
    reviewOptions: { needed: 'Needs review', revised: 'Revised' },
    // Talep türü (ADR 0122 §6)
    kind: 'Type',
    allKinds: 'All Types',
    kindOptions: { provision: 'Provisions', stores: 'Stores & Spares' },
  },
  form: {
    newTitle: 'New Material Requisition',
    linked: 'Linked to {{no}}: the requisition is shown on that record (Material).',
    editTitle: 'Edit {{no}}',
    newCrumb: 'New',
    description: 'Items needed on board; the approval follows the amount bands of the company.',
    header: 'Header Information',
    items: 'Add Items',
    vessel: 'Vessel',
    department: 'Department',
    requestedBy: 'Requested By',
    requiredDate: 'Required Date',
    port: 'Port',
    portPlaceholder: 'Delivery port',
    remarks: 'Remarks',
    reason: 'Reason for Purchase',
    choose: 'Select',
    addMaterial: 'Add Material',
    columns: {
      no: '#',
      material: 'Material',
      description: 'Description',
      qty: 'Qty',
      uom: 'UOM',
      unitPrice: 'Unit Price ({{currency}})',
      total: 'Total ({{currency}})',
      priority: 'Priority',
      actions: 'Actions',
    },
    remove: 'Remove {{name}}',
    noItems: 'No items yet. Add materials from the catalogue.',
    total: 'Total',
    unknownTotal: 'Total of priced items; some items have no price.',
    cancel: 'Cancel',
    saveDraft: 'Save Draft',
    saveSubmit: 'Save & Submit',
    saved: 'Requisition {{no}} saved as draft.',
    submitted: 'Requisition {{no}} submitted for approval.',
    loading: 'Loading the form…',
    error: 'The form could not be loaded.',
    fieldsInvalid: 'Check the highlighted fields.',
  },
  picker: {
    error: 'The catalogue could not be loaded.',
    title: 'Add Material',
    search: 'Search material',
    searchPlaceholder: 'Search material, category, code…',
    results: 'Catalogue results',
    add: 'Add {{name}}',
    none: 'No material matches.',
    freeTitle: 'Item not in the catalogue',
    freeName: 'Item name',
    freeUnit: 'UOM',
    freeAdd: 'Add item',
    close: 'Close',
    catalogueTab: 'Catalogue',
    pasteTab: 'Paste from Excel',
    select: 'Select {{name}}',
    quantityOf: 'Quantity of {{name}}',
    unitOf: 'UOM of {{name}}',
    selectedCount_one: '{{count}} item selected',
    selectedCount_other: '{{count}} items selected',
    addSelected_one: 'Add {{count}} item',
    addSelected_other: 'Add {{count}} items',
    limit: 'A requisition has at most 200 lines; {{count}} more can be added.',
    pasteLabel: 'Rows copied from Excel or CSV',
    pasteHint:
      'Columns: code or name, quantity, UOM (optional). Codes match the company, IMPA or ISSA code; names must match one catalogue item.',
    pastePlaceholder: 'CHK-01	40	KG',
    headerSkipped: 'The header row was skipped.',
    truncated: 'Only the first {{count}} rows were read.',
    matchRows: 'Match with catalogue',
    nothingPasted: 'No rows to add.',
    proposals: 'Pasted rows',
    include: 'Add',
    includeRow: 'Add {{name}}',
    pasted: 'Pasted',
    catalogueItem: 'Catalogue item',
    qty: 'Qty',
    freeProposal: 'Not in catalogue — free item',
    unitFromCatalogue: 'UOM from the catalogue: {{unit}}',
    unknownUnit: 'Unknown UOM "{{unit}}"; choose one.',
    matchedSummary: '{{matched}} matched, {{free}} free items (tick to add)',
    addLines_one: 'Add {{count}} line',
    addLines_other: 'Add {{count}} lines',
  },
  password: {
    submitTitle: 'Sign and submit',
    submitBody:
      'Submitting signs the requisition in your name. It is locked until it is approved or returned.',
    approveTitle: 'Approve requisition',
    approveBody: 'Approve {{no}}. Your name, role and the time are recorded.',
    returnTitle: 'Return requisition',
    returnBody: 'Return {{no}} to the requester with a reason.',
    cancelTitle: 'Cancel requisition',
    cancelBody: 'The requisition is kept with the reason; it cannot be edited again.',
    reason: 'Reason',
    comment: 'Comment',
    password: 'Password',
    confirm: 'Confirm',
    back: 'Back',
  },
  fulfilment: {
    title: 'Requested vs Supplied',
    requested: 'Requested',
    approved: 'Approved',
    ordered: 'Ordered',
    accepted: 'Accepted',
    damaged: 'Damaged',
    missing: 'Missing',
    outstanding: 'Outstanding',
    unitPrice: 'Unit Price',
    orders: 'PO',
    status: 'Status',
    notRequested: 'Not on the requisition',
    noOrders: 'No purchase order yet.',
    noOrdersHint:
      'Ordered and delivered quantities appear here once the office issues a purchase order.',
    openOrders:
      'Still open: {{orders}}. Outstanding quantities may still be delivered against this order.',
    createShortfall: 'Create shortfall requisition',
    statuses: {
      not_ordered: 'Not ordered',
      pending: 'Awaiting delivery',
      partial: 'Partly supplied',
      complete: 'Supplied',
    },
  },
  // Ofisin talep incelemesi ve onaylı liste (ADR 0111 §A; sahip 2026-10-23)
  review: {
    officeTab: 'Office Review',
    approvedTab: 'Approved List',
    officeIntro:
      'Edit the approved requisition before the RFQ: change quantities, remove items or add catalogue items. The vessel receives the approved list and follows it.',
    columns: {
      no: '#',
      item: 'Item',
      requested: 'Requested',
      inStock: 'In Stock',
      approved: 'Approved',
      uom: 'UOM',
      remove: 'Remove',
      note: 'Note',
    },
    approvedOf: 'Approved quantity of {{name}}',
    removeOf: 'Remove {{name}} from the list',
    noteOf: 'Note for {{name}}',
    removeAdded: 'Take {{name}} off the added items',
    addItem: 'Add Catalogue Item',
    added: 'Added by office',
    removed: 'Removed',
    reason: 'Reason for the changes',
    reasonHint: 'The vessel sees this reason with the approved list.',
    send: 'Send to Vessel',
    reset: 'Reset',
    changes_one: '{{count}} change',
    changes_other: '{{count}} changes',
    noChanges: 'No changes yet.',
    sent: 'Approved list (revision {{no}}) sent to the vessel.',
    confirmTitle: 'Send approved list',
    confirmBody:
      'The vessel receives this list and follows it. The revision is recorded with your name and cannot be edited; a later revision replaces it.',
    revisionLine: 'Revision {{no}} · {{at}}',
    revisionBy: 'by {{name}}',
    noRevision: 'The office has not changed this requisition; it is approved as requested.',
    legend: 'Changed quantities are highlighted; removed items are struck through.',
    inStock: 'In stock: {{qty}}',
    quantityChange: '{{from}} → {{to}}',
    table: 'Approved list items',
    grid: 'Office review items',
  },
  workflow: {
    itemTabs: 'Requisition items',
    title: 'Approval Workflow',
    description:
      'Approval steps of the requisition by amount band; office steps are decided ashore.',
    strip: 'Approval steps',
    labels: {
      draft: 'Draft',
      requested: 'Requested',
      approved: 'Approved',
      returned: 'Returned',
      pending: 'Pending',
      officePending: 'Awaiting Office',
      waiting: 'Waiting',
      escalated: 'Moved to office',
      poIssued: 'PO Issued',
    },
    // Onay durumu (ADR 0091): kim bekliyor, karar nerede
    approvalStatus: 'Approval status',
    notes: {
      escalated:
        'Submitted by the {{shipRole}}: under the four-eyes rule the {{shipRole}} cannot approve their own requisition, so this approval is decided in the office by the {{role}}.',
      ownEscalated:
        'You submitted this requisition. As {{shipRole}} you cannot approve it yourself (four-eyes rule); approval goes to the {{role}} in the office.',
      awaitingShip: 'Awaiting {{role}} approval (on board).',
      awaitingOffice: 'Awaiting office approval: {{role}}.',
      decideHere: 'Your decision is needed: use Approve or Return at the top of this page.',
      decideByRole:
        'A user with the {{role}} role decides this step in the office with Approve or Return on this page.',
      decidedOnBoard:
        'This step is decided on board by the {{role}}; the office sees the decision after the next synchronisation.',
      ownWaiting: 'You submitted this requisition; the {{role}} on board approves it.',
      officeWaiting:
        'The office decides this step. The decision reaches the vessel with the next synchronisation.',
    },
    office: 'Office',
    officeWaiting:
      'Waiting for the office approval. The decision reaches the vessel with the next synchronisation.',
    comments: 'Approval Comments',
    noComments: 'No comments yet.',
    noComment: 'No comment.',
    details: 'Requisition Details',
    items: 'Items',
    requestDate: 'Request Date',
    total: 'Total',
    approve: 'Approve',
    return: 'Return',
    edit: 'Edit',
    submit: 'Submit',
    cancelRequisition: 'Cancel Requisition',
    createRfq: 'Create RFQ',
    openRfq: 'Open {{no}}',
    openOrder: 'Open {{no}}',
    back: 'Back to list',
    cancelled: 'Cancelled: {{reason}}',
    approved: 'Requisition {{no}} approved.',
    returned: 'Requisition {{no}} returned.',
    cancelledDone: 'Requisition {{no}} cancelled.',
    loading: 'Loading the requisition…',
    error: 'The requisition could not be loaded.',
  },
  suppliers: {
    title: 'Suppliers',
    description:
      'Approved, preferred and emergency suppliers of the company with contact and terms.',
    search: 'Search supplier name…',
    new: 'New Supplier',
    table: 'Suppliers',
    columns: {
      name: 'Supplier Name',
      contact: 'Contact Person',
      phone: 'Phone',
      email: 'Email',
      portal: 'Supplier Portal',
      status: 'Status',
      actions: 'Actions',
    },
    active: 'Active',
    inactive: 'Inactive',
    portal: {
      status: {
        none: 'Not invited',
        invited: 'Invited',
        active: 'Active',
        revoked: 'Revoked',
        prospect: 'Self-registered',
      },
      verified: 'Verified',
      suspended: 'Suspended by platform',
      inviteAction: 'Invite to portal',
      resendAction: 'Resend invitation',
      revokeAction: 'Revoke portal access',
      inviteTitle: 'Invite {{name}} to the Supplier Portal',
      resendTitle: 'Resend the invitation to {{name}}',
      revokeTitle: 'Revoke portal access of {{name}}',
      inviteText:
        'The supplier receives a sign-up link by e-mail. The link is valid for 24 hours and can be used once. Quotations and orders are shared through the portal only after the invitation is accepted.',
      resendText:
        'A new link is sent and the previous invitation link stops working. The link is valid for 24 hours.',
      revokeText:
        'The supplier can no longer see this company in the portal. The account and its history are kept; you can invite the supplier again later.',
      email: 'Invitation e-mail',
      language: 'E-mail language',
      reason: 'Reason',
      send: 'Send invitation',
      resend: 'Send new link',
      revokeConfirm: 'Revoke access',
      invited: 'Invitation sent to {{email}}.',
      revoked: 'Portal access of {{name}} revoked.',
      emailInvalid: 'Enter a valid e-mail address.',
      reasonShort: 'Write at least 5 characters.',
      errors: {
        SUPPLIER_NOT_ELIGIBLE: 'Only approved and active suppliers can be invited.',
        PORTAL_ALREADY_ACTIVE: 'This supplier already uses the portal.',
        PORTAL_NOT_INVITED: 'This supplier has no open invitation or portal access.',
        PORTAL_UNAVAILABLE: 'The Supplier Portal is not available on this server.',
      },
    },
    actionsOf: 'Actions for {{name}}',
    edit: 'Edit',
    deactivate: 'Deactivate',
    activate: 'Activate',
    showing: 'Showing {{from}} - {{to}} of {{total}}',
    pagination: 'Supplier pages',
    emptyTitle: 'No suppliers yet.',
    emptyText: 'Add the suppliers the company buys from.',
    noMatch: 'No supplier matches the search.',
    error: 'The suppliers could not be loaded.',
    saved: 'Supplier {{name}} saved.',
    types: {
      Manufacturer: 'Manufacturer',
      Distributor: 'Distributor',
      'Service Provider': 'Service Provider',
    },
    flags: { approved: 'Approved', preferred: 'Preferred', emergency: 'Emergency' },
    dialog: {
      newTitle: 'New Supplier',
      editTitle: 'Edit {{name}}',
      general: 'Supplier',
      contact: 'Contact',
      commercial: 'Commercial',
      name: 'Supplier Name',
      code: 'Supplier Code',
      codeAuto: 'Given by the system',
      type: 'Supplier Type',
      country: 'Country',
      city: 'City',
      address: 'Address',
      contactPerson: 'Contact Person',
      email: 'Email',
      phone: 'Telephone',
      website: 'Website',
      approved: 'Approved supplier',
      preferred: 'Preferred supplier',
      emergency: 'Emergency supplier',
      active: 'Active',
      paymentTerms: 'Payment Terms',
      currency: 'Currency',
      bankInfo: 'Bank Information',
      taxInfo: 'Tax Information',
      rating: 'Performance Rating (1–5)',
      remarks: 'Remarks',
      cancel: 'Cancel',
      save: 'Save',
    },
  },
  rfq: {
    title: 'RFQ',
    description:
      'Requests for quotation opened from approved requisitions; quotations are compared and a supplier is selected.',
    table: 'Requests for quotation',
    columns: {
      rfqNo: 'RFQ No',
      requisitionNo: 'Requisition No',
      vessel: 'Vessel',
      requestDate: 'Request Date',
      closingDate: 'Closing Date',
      suppliers: 'Suppliers',
      quotations: 'Quotations',
      best: 'Best Price',
      status: 'Status',
    },
    open: 'Open {{no}}',
    emptyTitle: 'No RFQs yet.',
    emptyText: 'Open an RFQ from an approved requisition.',
    error: 'The RFQs could not be loaded.',
    retry: 'Retry',
    showing: 'Showing {{from}} - {{to}} of {{total}}',
    pagination: 'RFQ pages',
    statuses: {
      draft: 'Draft',
      sent: 'Sent to Suppliers',
      selected: 'Supplier Selected',
      cancelled: 'Cancelled',
    },
    detail: {
      title: 'RFQ & Supplier Comparison',
      description:
        'RFQ items, supplier quotations and their comparison by total landed cost and lead time.',
      created: 'Created: {{date}}',
      tabsLabel: 'RFQ sections',
      tabs: {
        details: 'RFQ Details',
        quotations: 'Quotations ({{count}})',
        comparison: 'Comparison',
        lines: 'Line Comparison',
        award: 'Award',
        comments: 'Comments',
      },
      info: 'RFQ Information',
      items: 'RFQ Items',
      quotations: 'Supplier Quotations',
      invited: 'Invited Suppliers',
      columns: {
        no: '#',
        material: 'Material',
        qty: 'Qty',
        uom: 'UOM',
        requiredDate: 'Required Date',
        supplier: 'Supplier',
        totalPrice: 'Total Price ({{currency}})',
        leadTime: 'Lead Time',
        validity: 'Validity',
        status: 'Status',
        ref: 'Quotation Ref',
        received: 'Received',
        items: 'Items',
        discount: 'Discount',
        freight: 'Freight',
        customs: 'Customs',
        tax: 'Tax',
        serviceFee: 'Service Fee',
        landed: 'Total Landed Cost',
        warranty: 'Warranty',
        rating: 'Supplier Rating',
        email: 'Email',
        quotation: 'Quotation',
        actions: 'Actions',
      },
      days_one: '{{count}} day',
      days_other: '{{count}} days',
      bestPrice: 'Best Price',
      fastest: 'Fastest',
      selected: 'Selected',
      late: 'Late',
      quoted: 'Quoted',
      notQuoted: 'Awaiting',
      excluded: {
        INCOMPLETE_QUOTATION: 'Incomplete',
        QUOTATION_CURRENCY: 'Other currency',
        SUPPLIER_NOT_APPROVED: 'Supplier not approved',
      },
      compareSelect: 'Compare & Select',
      portal: {
        label: 'Supplier Portal',
        open: 'Open',
        invited: 'Invited suppliers',
        closes: 'Closes {{date}}',
        closed: 'Closed {{date}}',
        left: '{{days}} d {{hours}} h left',
        publish: 'Publish to Portal',
        publishTitle: 'Publish to the supplier portal',
        publishBody:
          'Suppliers answer each item on the portal (price, no price, decline or substitute) and submit from their verified account. The deadline cannot be changed after publishing.',
        visibility: 'Who can quote',
        openOption: 'Open',
        openHint:
          'Every supplier registered on the portal with a confirmed e-mail can see and quote. Company and vessel names are hidden from suppliers that are not yours.',
        invitedOption: 'Invited suppliers',
        invitedHint: 'Only the suppliers invited to this RFQ: {{names}}',
        deadline: 'Deadline',
        date: 'Date',
        time: 'Time',
        timeHint: 'Your local time ({{zone}}). Stored in UTC.',
        confirm: 'Publish',
        fieldErrors: {
          REQUIRED: 'Required.',
          INVALID_DATE: 'Enter a valid date and time.',
          CLOSING_IN_PAST: 'The deadline must be in the future.',
        },
      },
      lines: {
        title: 'Line Comparison',
        note: 'Best is the lowest unit price per item among priced answers in {{currency}}. Substitutes are marked separately and never count as best.',
        empty: 'No quotations to compare yet.',
        best: 'Best',
        substitute: 'Substitute',
        declined: 'Declined',
        noPrice: 'No price',
        noAnswer: 'No answer',
        noteLabel: 'Note',
        bestTotal: 'Best per line',
        covered: 'Items with a best price',
        bestCount: '{{count}} best',
        otherCurrency: 'Other currency',
        notApproved: 'Supplier not approved',
      },
      source: {
        portal: 'Portal',
        office: 'Office',
      },
      revision: 'Rev {{no}}',
      // Ödül, bölme ve değişiklik turu (ADR 0111 §D)
      award: awardEn,
      revisions: {
        title: 'Revision history · {{supplier}}',
        show: 'Revisions of {{supplier}}',
        no: 'Rev.',
        submitted: 'Submitted',
        total: 'Total Landed Cost',
        submittedBy: 'Submitted By',
        hash: 'Content hash',
        current: 'Current',
      },
      createPo: 'Create PO',
      openOrder: 'Open {{no}}',
      send: 'Send to Suppliers',
      sendTitle: 'Send to suppliers',
      sendBody:
        'Records that the RFQ was sent to the invited suppliers. Send the RFQ from your e-mail; quotations are entered here.',
      invite: 'Invite Supplier',
      inviteTitle: 'Invite supplier',
      inviteBody: 'Only approved and active suppliers can be invited.',
      inviteSupplier: 'Supplier',
      inviteConfirm: 'Invite',
      enterQuotation: 'Enter Quotation',
      editQuotation: 'Edit Quotation',
      quotationOf: 'Quotation of {{supplier}}',
      select: 'Select',
      selectOf: 'Select {{supplier}}',
      selectTitle: 'Select {{supplier}}',
      selectBody: 'The selected quotation closes the RFQ. The purchase order is issued from it.',
      selectReasonBody:
        'This quotation is not the best price. Enter the reason for selection; it is kept with the RFQ.',
      selectReason: 'Reason for selection',
      cancel: 'Cancel RFQ',
      cancelTitle: 'Cancel RFQ',
      cancelBody:
        'The RFQ is kept with the reason and cannot be changed. A new RFQ can be opened from the requisition.',
      requisition: 'Requisition',
      vessel: 'Vessel',
      requestDate: 'Request Date',
      closingDate: 'Closing Date',
      currency: 'Currency',
      deliveryLocation: 'Delivery Location',
      deliveryRequirement: 'Delivery Requirement',
      paymentTerms: 'Payment Terms',
      warranty: 'Warranty',
      validity: 'Validity',
      technicalSpec: 'Technical Specification',
      noQuotations: 'No quotations yet.',
      noQuotationsText: 'Enter the quotations received from the invited suppliers.',
      comparisonEmpty: 'Enter quotations to compare.',
      comparisonNote:
        'Best Price is the lowest total landed cost among complete quotations in {{currency}}.',
      selectedNote: '{{supplier}} selected: {{reason}}',
      selectedNoReason: '{{supplier}} selected (best price).',
      cancelledNote: 'Cancelled: {{reason}}',
      events: {
        created: 'RFQ created',
        sent: 'Sent to suppliers',
        published: 'Published to the supplier portal',
        quotation: 'Quotation received from {{supplier}}',
        selected: '{{supplier}} selected',
        cancelled: 'RFQ cancelled',
      },
      noComments: 'No comments yet.',
      notices: {
        created: 'RFQ {{no}} created.',
        sent: 'RFQ {{no}} marked as sent to suppliers.',
        published: 'RFQ {{no}} published to the supplier portal.',
        invited: '{{supplier}} invited.',
        quoted: 'Quotation of {{supplier}} saved.',
        selected: '{{supplier}} selected.',
        cancelled: 'RFQ {{no}} cancelled.',
      },
      openRequisition: 'Open requisition {{no}}',
      loading: 'Loading the RFQ…',
      error: 'The RFQ could not be loaded.',
      back: 'Back to list',
    },
    quotation: {
      titleNew: 'Quotation · {{supplier}}',
      titleEdit: 'Edit quotation · {{supplier}}',
      header: 'Quotation',
      ref: 'Quotation Ref',
      received: 'Received Date',
      currency: 'Currency',
      leadTime: 'Lead Time (days)',
      validity: 'Validity (days)',
      warranty: 'Warranty',
      prices: 'Unit Prices',
      unitPrice: 'Unit Price ({{currency}})',
      priceOf: 'Unit price of {{name}}',
      lineTotal: 'Total',
      costs: 'Costs',
      discount: 'Discount',
      freight: 'Freight',
      customs: 'Customs',
      tax: 'Tax',
      serviceFee: 'Service Fee',
      remarks: 'Remarks',
      itemsTotal: 'Items total',
      landedTotal: 'Total landed cost',
      incomplete: 'Every item must be priced for the quotation to be compared.',
      save: 'Save Quotation',
      cancel: 'Cancel',
    },
    create: {
      title: 'Create RFQ · {{no}}',
      general: 'RFQ',
      suppliers: 'Suppliers',
      suppliersPlaceholder: 'Choose suppliers',
      suppliersHint: 'Only approved and active suppliers can be invited.',
      items: 'Items',
      itemOf: 'Include {{name}}',
      closingDate: 'Closing Date',
      currency: 'Currency',
      deliveryLocation: 'Delivery Location',
      deliveryRequirement: 'Delivery Requirement',
      paymentTerms: 'Payment Terms',
      warranty: 'Warranty',
      validity: 'Validity (days)',
      technicalSpec: 'Technical Specification',
      create: 'Create RFQ',
      cancel: 'Cancel',
      loading: 'Loading suppliers…',
    },
  },
  errors: errorsEn,
  field: fieldEn,
  po: poEn,
  grn: grnEn,
};

export const purchasingTr: typeof purchasingEn = {
  table: { sortBy: '{{column}} sütununa göre sırala', actions: 'İşlemler' },
  crumb: 'Satın Alma',
  statuses: {
    draft: 'Taslak',
    pending_approval: 'Onay Bekliyor',
    office_approval: 'Ofis Onayı Bekliyor',
    approved: 'Onaylandı',
    returned: 'İade Edildi',
    cancelled: 'İptal Edildi',
    in_progress: 'İşlemde',
    po_issued: 'Sipariş Verildi',
    closed: 'Kapandı',
  },
  departments: {
    Deck: 'Güverte',
    Engine: 'Makine',
    Electrical: 'Elektrik',
    Navigation: 'Seyir',
    Galley: 'Mutfak',
    Cabin: 'Kabin',
    Safety: 'Emniyet',
    Medical: 'Sağlık',
    Provisions: 'Kumanya',
    Other: 'Diğer',
  },
  priorities: { Normal: 'Normal', High: 'Yüksek', Urgent: 'Acil' },
  reasons: {
    Maintenance: 'Bakım',
    Safety: 'Emniyet',
    Navigation: 'Seyir',
    Deck: 'Güverte',
    Engine: 'Makine',
    Hotel: 'Otel',
    Galley: 'Mutfak',
    Crew: 'Mürettebat',
    Provision: 'Kumanya',
    'Spare Parts': 'Yedek Parça',
    Survey: 'Sörvey',
    Audit: 'Denetim',
    Deficiency: 'Eksiklik',
    'Planned Maintenance': 'Planlı Bakım',
    Emergency: 'Acil Durum',
  },
  bell: {
    title: 'Satın alma kararları',
    requisition: 'Onayınızı bekleyen talep',
    escalated: 'onaylayıcının kendi talebi',
    approvedList: 'Ofisten onaylı liste geldi',
    revision: 'revizyon {{no}}',
  },
  list: {
    title: 'Malzeme Talebi',
    descriptionShip: 'Geminin talepleri ve onay durumları.',
    descriptionWeb: 'Filonun talepleri; ofisteki onay adımları burada karara bağlanır.',
    new: 'Yeni Talep',
    vessel: 'Gemi',
    allVessels: 'Bütün Gemiler',
    status: 'Durum',
    allStatus: 'Bütün Durumlar',
    dateRange: 'Tarih Aralığı',
    dateRangePlaceholder: 'Talep tarihi',
    dateFrom: 'Talep tarihi başlangıç',
    dateTo: 'Talep tarihi bitiş',
    filters: 'Talep süzgeçleri',
    table: 'Talepler',
    columns: {
      no: '#',
      requisitionNo: 'Talep No',
      vessel: 'Gemi',
      department: 'Bölüm',
      requestedBy: 'Talep Eden',
      requiredDate: 'İstenen Tarih',
      status: 'Durum',
      total: 'Toplam ({{currency}})',
    },
    open: '{{no}} aç',
    awaitingShip: '{{role}} · gemide',
    awaitingOffice: '{{role}} · ofiste',
    showing: '{{total}} kayıttan {{from}}-{{to}}',
    pagination: 'Talep sayfaları',
    emptyTitle: 'Henüz talep yok.',
    emptyShip: 'Geminin ihtiyacı olan kalemler için yeni talep açın.',
    emptyWeb: 'Talepler gemide açılır; senkrondan sonra burada görünür.',
    noMatch: 'Süzgeçlere uyan talep yok.',
    error: 'Talepler yüklenemedi.',
    retry: 'Yeniden dene',
    unknownTotal: 'Fiyatsız',
    inStock: 'Stokta',
    inStockLines: '{{total}} kalemden {{count}}',
    review: 'İnceleme',
    allReview: 'Tümü',
    kind: 'Tür',
    allKinds: 'Bütün Türler',
    kindOptions: { provision: 'Kumanya', stores: 'Malzeme ve Yedek Parça' },
    reviewOptions: { needed: 'İnceleme bekliyor', revised: 'Düzenlendi' },
  },
  form: {
    newTitle: 'Yeni Malzeme Talebi',
    linked: '{{no}} kaydına bağlı: talep o kaydın Malzeme bölümünde görünür.',
    editTitle: '{{no}} düzelt',
    newCrumb: 'Yeni',
    description: 'Gemide gereken kalemler; onay şirketin tutar kademelerine göre yürür.',
    header: 'Başlık Bilgileri',
    items: 'Kalem Ekle',
    vessel: 'Gemi',
    department: 'Bölüm',
    requestedBy: 'Talep Eden',
    requiredDate: 'İstenen Tarih',
    port: 'Liman',
    portPlaceholder: 'Teslim limanı',
    remarks: 'Açıklama',
    reason: 'Satın Alma Nedeni',
    choose: 'Seçin',
    addMaterial: 'Malzeme Ekle',
    columns: {
      no: '#',
      material: 'Malzeme',
      description: 'Tanım',
      qty: 'Miktar',
      uom: 'Birim',
      unitPrice: 'Birim Fiyat ({{currency}})',
      total: 'Toplam ({{currency}})',
      priority: 'Öncelik',
      actions: 'İşlemler',
    },
    remove: '{{name}} kaldır',
    noItems: 'Henüz kalem yok. Katalogdan malzeme ekleyin.',
    total: 'Toplam',
    unknownTotal: 'Fiyatlı kalemlerin toplamı; bazı kalemlerin fiyatı yok.',
    cancel: 'Vazgeç',
    saveDraft: 'Taslak Kaydet',
    saveSubmit: 'Kaydet ve Gönder',
    saved: '{{no}} taslak olarak kaydedildi.',
    submitted: '{{no}} onaya gönderildi.',
    loading: 'Form yükleniyor…',
    error: 'Form yüklenemedi.',
    fieldsInvalid: 'İşaretli alanları denetleyin.',
  },
  picker: {
    error: 'Katalog okunamadı.',
    title: 'Malzeme Ekle',
    search: 'Malzeme ara',
    searchPlaceholder: 'Malzeme, kategori, kod ara…',
    results: 'Katalog sonuçları',
    add: '{{name}} ekle',
    none: 'Uyan malzeme yok.',
    freeTitle: 'Katalogda olmayan kalem',
    freeName: 'Kalem adı',
    freeUnit: 'Birim',
    freeAdd: 'Kalemi ekle',
    close: 'Kapat',
    catalogueTab: 'Katalog',
    pasteTab: "Excel'den yapıştır",
    select: '{{name}} seç',
    quantityOf: '{{name}} miktarı',
    unitOf: '{{name}} birimi',
    selectedCount_one: '{{count}} kalem seçildi',
    selectedCount_other: '{{count}} kalem seçildi',
    addSelected_one: '{{count}} kalemi ekle',
    addSelected_other: '{{count}} kalemi ekle',
    limit: 'Talep en çok 200 satırdır; {{count}} satır daha eklenebilir.',
    pasteLabel: "Excel ya da CSV'den kopyalanan satırlar",
    pasteHint:
      'Sütunlar: kod ya da ad, miktar, birim (isteğe bağlı). Kod şirket, IMPA ya da ISSA koduyla; ad tek katalog kalemiyle eşleşmeli.',
    pastePlaceholder: 'CHK-01	40	KG',
    headerSkipped: 'Başlık satırı atlandı.',
    truncated: 'Yalnız ilk {{count}} satır okundu.',
    matchRows: 'Katalogla eşle',
    nothingPasted: 'Eklenecek satır yok.',
    proposals: 'Yapıştırılan satırlar',
    include: 'Ekle',
    includeRow: '{{name}} ekle',
    pasted: 'Yapıştırılan',
    catalogueItem: 'Katalog kalemi',
    qty: 'Miktar',
    freeProposal: 'Katalogda yok — serbest kalem',
    unitFromCatalogue: 'Birim katalogdan: {{unit}}',
    unknownUnit: 'Tanınmayan birim "{{unit}}"; seçin.',
    matchedSummary: '{{matched}} eşleşti, {{free}} serbest kalem (eklemek için işaretleyin)',
    addLines_one: '{{count}} satır ekle',
    addLines_other: '{{count}} satır ekle',
  },
  password: {
    submitTitle: 'İmzala ve gönder',
    submitBody:
      'Gönderim talebi adınıza imzalar. Talep onaylanana ya da iade edilene kadar kilitlenir.',
    approveTitle: 'Talebi onayla',
    approveBody: '{{no}} talebini onaylayın. Adınız, rolünüz ve saat kaydedilir.',
    returnTitle: 'Talebi iade et',
    returnBody: '{{no}} talebini gerekçeyle talep edene iade edin.',
    cancelTitle: 'Talebi iptal et',
    cancelBody: 'Talep gerekçesiyle saklanır; bir daha düzeltilemez.',
    reason: 'Gerekçe',
    comment: 'Yorum',
    password: 'Parola',
    confirm: 'Onayla',
    back: 'Geri',
  },
  fulfilment: {
    title: 'Talep vs Temin',
    requested: 'İstenen',
    approved: 'Onaylanan',
    ordered: 'Sipariş',
    accepted: 'Kabul',
    damaged: 'Hasarlı',
    missing: 'Eksik',
    outstanding: 'Kalan',
    unitPrice: 'Birim Fiyat',
    orders: 'PO',
    status: 'Durum',
    notRequested: 'Talepte yok',
    noOrders: 'Henüz sipariş yok.',
    noOrdersHint: 'Ofis sipariş verince sipariş ve teslim miktarları burada görünür.',
    openOrders: 'Açık sipariş: {{orders}}. Kalan miktarlar bu siparişle hâlâ gelebilir.',
    createShortfall: 'Eksik için yeni talep',
    statuses: {
      not_ordered: 'Sipariş yok',
      pending: 'Teslim bekleniyor',
      partial: 'Kısmen temin',
      complete: 'Temin edildi',
    },
  },
  review: {
    officeTab: 'Ofis İncelemesi',
    approvedTab: 'Onaylı Liste',
    officeIntro:
      'Teklif isteğinden önce onaylı talebi düzenleyin: miktarı değiştirin, kalemi çıkarın ya da katalog kalemi ekleyin. Gemiye onaylı liste iner ve gemi bu listeye göre hareket eder.',
    columns: {
      no: '#',
      item: 'Kalem',
      requested: 'İstenen',
      inStock: 'Stokta',
      approved: 'Onaylanan',
      uom: 'Birim',
      remove: 'Çıkar',
      note: 'Not',
    },
    approvedOf: '{{name}} onaylanan miktar',
    removeOf: '{{name}} listeden çıkar',
    noteOf: '{{name}} notu',
    removeAdded: '{{name}} eklenenlerden çıkar',
    addItem: 'Katalog Kalemi Ekle',
    added: 'Ofis ekledi',
    removed: 'Çıkarıldı',
    reason: 'Değişikliklerin gerekçesi',
    reasonHint: 'Gemi bu gerekçeyi onaylı listeyle birlikte görür.',
    send: 'Gemiye Gönder',
    reset: 'Sıfırla',
    changes_one: '{{count}} değişiklik',
    changes_other: '{{count}} değişiklik',
    noChanges: 'Henüz değişiklik yok.',
    sent: 'Onaylı liste (revizyon {{no}}) gemiye gönderildi.',
    confirmTitle: 'Onaylı listeyi gönder',
    confirmBody:
      'Gemiye bu liste iner ve gemi buna göre hareket eder. Revizyon adınızla kaydedilir ve değiştirilemez; sonraki revizyon onun yerine geçer.',
    revisionLine: 'Revizyon {{no}} · {{at}}',
    revisionBy: '{{name}}',
    noRevision: 'Ofis bu talebi değiştirmedi; talep istendiği gibi onaylı.',
    legend: 'Değişen miktarlar renklidir; çıkarılan kalemlerin üstü çizilidir.',
    inStock: 'Stokta: {{qty}}',
    quantityChange: '{{from}} → {{to}}',
    table: 'Onaylı liste kalemleri',
    grid: 'Ofis incelemesi kalemleri',
  },
  workflow: {
    itemTabs: 'Talep kalemleri',
    title: 'Onay Akışı',
    description:
      'Talebin tutar kademesine göre onay adımları; ofis adımları karada karara bağlanır.',
    strip: 'Onay adımları',
    labels: {
      draft: 'Taslak',
      requested: 'Talep Edildi',
      approved: 'Onaylandı',
      returned: 'İade Edildi',
      pending: 'Bekliyor',
      officePending: 'Ofiste Bekliyor',
      waiting: 'Sırada',
      escalated: 'Ofise geçti',
      poIssued: 'Sipariş Verildi',
    },
    approvalStatus: 'Onay durumu',
    notes: {
      escalated:
        'Talebi {{shipRole}} gönderdi: dört göz kuralı gereği {{shipRole}} kendi talebini onaylayamaz; bu onay ofiste {{role}} tarafından verilir.',
      ownEscalated:
        'Bu talebi siz gönderdiniz. {{shipRole}} olarak kendi talebinizi onaylayamazsınız (dört göz kuralı); onay ofiste {{role}} rolüne gider.',
      awaitingShip: '{{role}} onayı bekleniyor (gemide).',
      awaitingOffice: 'Ofis onayı bekleniyor: {{role}}.',
      decideHere:
        'Kararınız bekleniyor: sayfanın üstündeki Onayla ya da İade Et düğmesini kullanın.',
      decideByRole:
        'Bu adımı ofiste {{role}} rolündeki kullanıcı bu sayfadaki Onayla ya da İade Et ile karara bağlar.',
      decidedOnBoard:
        'Bu adım gemide {{role}} tarafından karara bağlanır; ofis kararı bir sonraki senkronla görür.',
      ownWaiting: 'Bu talebi siz gönderdiniz; gemide {{role}} onaylar.',
      officeWaiting: 'Bu adımı ofis karara bağlar. Karar bir sonraki senkronla gemiye iner.',
    },
    office: 'Ofis',
    officeWaiting: 'Ofis onayı bekleniyor. Karar bir sonraki senkronla gemiye iner.',
    comments: 'Onay Yorumları',
    noComments: 'Henüz yorum yok.',
    noComment: 'Yorum yok.',
    details: 'Talep Bilgileri',
    items: 'Kalemler',
    requestDate: 'Talep Tarihi',
    total: 'Toplam',
    approve: 'Onayla',
    return: 'İade Et',
    edit: 'Düzelt',
    submit: 'Gönder',
    cancelRequisition: 'Talebi İptal Et',
    createRfq: 'Teklif İsteği Aç',
    openRfq: '{{no}} aç',
    openOrder: '{{no}} aç',
    back: 'Listeye dön',
    cancelled: 'İptal edildi: {{reason}}',
    approved: '{{no}} onaylandı.',
    returned: '{{no}} iade edildi.',
    cancelledDone: '{{no}} iptal edildi.',
    loading: 'Talep yükleniyor…',
    error: 'Talep yüklenemedi.',
  },
  suppliers: {
    title: 'Tedarikçiler',
    description: 'Şirketin onaylı, tercihli ve acil tedarikçileri; iletişim ve koşullarıyla.',
    search: 'Tedarikçi adı ara…',
    new: 'Yeni Tedarikçi',
    table: 'Tedarikçiler',
    columns: {
      name: 'Tedarikçi Adı',
      contact: 'İlgili Kişi',
      phone: 'Telefon',
      email: 'E-posta',
      portal: 'Tedarikçi Portalı',
      status: 'Durum',
      actions: 'İşlemler',
    },
    active: 'Etkin',
    inactive: 'Pasif',
    portal: {
      status: {
        none: 'Davet edilmedi',
        invited: 'Davet edildi',
        active: 'Etkin',
        revoked: 'İptal edildi',
        prospect: 'Kendi kaydoldu',
      },
      verified: 'Doğrulanmış',
      suspended: 'Platformca askıda',
      inviteAction: 'Portala davet et',
      resendAction: 'Daveti yeniden gönder',
      revokeAction: 'Portal erişimini iptal et',
      inviteTitle: '{{name}} tedarikçisini portala davet et',
      resendTitle: '{{name}} tedarikçisine daveti yeniden gönder',
      revokeTitle: '{{name}} tedarikçisinin portal erişimini iptal et',
      inviteText:
        'Tedarikçiye e-postayla kayıt bağlantısı gider. Bağlantı 24 saat geçerlidir ve bir kez kullanılır. Teklif ve siparişler yalnız davet kabul edildikten sonra portaldan paylaşılır.',
      resendText:
        'Yeni bir bağlantı gider; önceki davet bağlantısı çalışmaz. Bağlantı 24 saat geçerlidir.',
      revokeText:
        'Tedarikçi bu şirketi portalda artık göremez. Hesap ve geçmişi korunur; tedarikçiyi sonra yeniden davet edebilirsiniz.',
      email: 'Davet e-postası',
      language: 'E-posta dili',
      reason: 'Gerekçe',
      send: 'Daveti gönder',
      resend: 'Yeni bağlantı gönder',
      revokeConfirm: 'Erişimi iptal et',
      invited: 'Davet {{email}} adresine gönderildi.',
      revoked: '{{name}} tedarikçisinin portal erişimi iptal edildi.',
      emailInvalid: 'Geçerli bir e-posta adresi girin.',
      reasonShort: 'En az 5 karakter yazın.',
      errors: {
        SUPPLIER_NOT_ELIGIBLE: 'Yalnız onaylı ve etkin tedarikçiler davet edilebilir.',
        PORTAL_ALREADY_ACTIVE: 'Bu tedarikçi portalı zaten kullanıyor.',
        PORTAL_NOT_INVITED: 'Bu tedarikçinin açık daveti ya da portal erişimi yok.',
        PORTAL_UNAVAILABLE: 'Tedarikçi portalı bu sunucuda kullanılamıyor.',
      },
    },
    actionsOf: '{{name}} işlemleri',
    edit: 'Düzelt',
    deactivate: 'Pasifleştir',
    activate: 'Etkinleştir',
    showing: '{{total}} kayıttan {{from}} - {{to}}',
    pagination: 'Tedarikçi sayfaları',
    emptyTitle: 'Henüz tedarikçi yok.',
    emptyText: 'Şirketin satın aldığı tedarikçileri ekleyin.',
    noMatch: 'Aramaya uyan tedarikçi yok.',
    error: 'Tedarikçiler yüklenemedi.',
    saved: '{{name}} kaydedildi.',
    types: {
      Manufacturer: 'Üretici',
      Distributor: 'Distribütör',
      'Service Provider': 'Hizmet Sağlayıcı',
    },
    flags: { approved: 'Onaylı', preferred: 'Tercihli', emergency: 'Acil' },
    dialog: {
      newTitle: 'Yeni Tedarikçi',
      editTitle: '{{name}} düzelt',
      general: 'Tedarikçi',
      contact: 'İletişim',
      commercial: 'Ticari',
      name: 'Tedarikçi Adı',
      code: 'Tedarikçi Kodu',
      codeAuto: 'Sistem verir',
      type: 'Tedarikçi Türü',
      country: 'Ülke',
      city: 'Şehir',
      address: 'Adres',
      contactPerson: 'İlgili Kişi',
      email: 'E-posta',
      phone: 'Telefon',
      website: 'Web Sitesi',
      approved: 'Onaylı tedarikçi',
      preferred: 'Tercihli tedarikçi',
      emergency: 'Acil tedarikçi',
      active: 'Etkin',
      paymentTerms: 'Ödeme Koşulları',
      currency: 'Para Birimi',
      bankInfo: 'Banka Bilgileri',
      taxInfo: 'Vergi Bilgileri',
      rating: 'Performans Puanı (1–5)',
      remarks: 'Açıklama',
      cancel: 'Vazgeç',
      save: 'Kaydet',
    },
  },
  rfq: {
    title: 'Teklif İsteği',
    description:
      'Onaylı taleplerden açılan teklif istekleri; teklifler karşılaştırılır ve tedarikçi seçilir.',
    table: 'Teklif istekleri',
    columns: {
      rfqNo: 'RFQ No',
      requisitionNo: 'Talep No',
      vessel: 'Gemi',
      requestDate: 'Talep Tarihi',
      closingDate: 'Kapanış Tarihi',
      suppliers: 'Tedarikçi',
      quotations: 'Teklif',
      best: 'En İyi Fiyat',
      status: 'Durum',
    },
    open: '{{no}} aç',
    emptyTitle: 'Henüz teklif isteği yok.',
    emptyText: 'Onaylı bir talepten teklif isteği açın.',
    error: 'Teklif istekleri yüklenemedi.',
    retry: 'Yeniden dene',
    showing: '{{total}} kayıttan {{from}} - {{to}} gösteriliyor',
    pagination: 'Teklif isteği sayfaları',
    statuses: {
      draft: 'Taslak',
      sent: 'Tedarikçilere Gönderildi',
      selected: 'Tedarikçi Seçildi',
      cancelled: 'İptal Edildi',
    },
    detail: {
      title: 'Teklif İsteği ve Tedarikçi Karşılaştırması',
      description:
        'Teklif isteğinin kalemleri, tedarikçi teklifleri ve toplam maliyet ile teslim süresine göre karşılaştırma.',
      created: 'Açılış: {{date}}',
      tabsLabel: 'Teklif isteği bölümleri',
      tabs: {
        details: 'RFQ Bilgileri',
        quotations: 'Teklifler ({{count}})',
        comparison: 'Karşılaştırma',
        lines: 'Satır Karşılaştırması',
        award: 'Ödül',
        comments: 'Yorumlar',
      },
      info: 'RFQ Bilgileri',
      items: 'RFQ Kalemleri',
      quotations: 'Tedarikçi Teklifleri',
      invited: 'Davetli Tedarikçiler',
      columns: {
        no: '#',
        material: 'Malzeme',
        qty: 'Miktar',
        uom: 'Birim',
        requiredDate: 'İstenen Tarih',
        supplier: 'Tedarikçi',
        totalPrice: 'Toplam Fiyat ({{currency}})',
        leadTime: 'Teslim Süresi',
        validity: 'Geçerlilik',
        status: 'Durum',
        ref: 'Teklif No',
        received: 'Geliş',
        items: 'Kalemler',
        discount: 'İndirim',
        freight: 'Navlun',
        customs: 'Gümrük',
        tax: 'Vergi',
        serviceFee: 'Hizmet Bedeli',
        landed: 'Toplam Teslim Maliyeti',
        warranty: 'Garanti',
        rating: 'Tedarikçi Puanı',
        email: 'E-posta',
        quotation: 'Teklif',
        actions: 'İşlemler',
      },
      days_one: '{{count}} gün',
      days_other: '{{count}} gün',
      bestPrice: 'En İyi Fiyat',
      fastest: 'En Hızlı',
      selected: 'Seçildi',
      late: 'Geç',
      quoted: 'Teklif verdi',
      notQuoted: 'Bekleniyor',
      excluded: {
        INCOMPLETE_QUOTATION: 'Eksik',
        QUOTATION_CURRENCY: 'Başka para birimi',
        SUPPLIER_NOT_APPROVED: 'Tedarikçi onaysız',
      },
      compareSelect: 'Karşılaştır ve Seç',
      portal: {
        label: 'Tedarikçi Portalı',
        open: 'Açık',
        invited: 'Davetli tedarikçiler',
        closes: 'Son tarih {{date}}',
        closed: 'Kapandı {{date}}',
        left: '{{days}} g {{hours}} sa kaldı',
        publish: 'Portalda Yayımla',
        publishTitle: 'Tedarikçi portalında yayımla',
        publishBody:
          'Tedarikçiler portalda her kalemi yanıtlar (fiyat, fiyatsız, ret ya da ikame) ve doğrulanmış hesabıyla gönderir. Yayımladıktan sonra son tarih değişmez.',
        visibility: 'Kimler teklif verebilir',
        openOption: 'Açık',
        openHint:
          'Portala kayıtlı, e-postası doğrulanmış her tedarikçi görür ve teklif verir. Sizin tedarikçiniz olmayanlara şirket ve gemi adı gösterilmez.',
        invitedOption: 'Davetli tedarikçiler',
        invitedHint: 'Yalnız bu teklif isteğine davetli tedarikçiler: {{names}}',
        deadline: 'Son tarih',
        date: 'Gün',
        time: 'Saat',
        timeHint: 'Yerel saatiniz ({{zone}}). UTC olarak saklanır.',
        confirm: 'Yayımla',
        fieldErrors: {
          REQUIRED: 'Zorunlu.',
          INVALID_DATE: 'Geçerli bir gün ve saat girin.',
          CLOSING_IN_PAST: 'Son tarih gelecekte olmalı.',
        },
      },
      lines: {
        title: 'Satır Karşılaştırması',
        note: 'En iyi, {{currency}} cinsinden fiyatlı yanıtlar içinde kalem başına en düşük birim fiyattır. İkameler ayrı işaretlenir ve en iyi sayılmaz.',
        empty: 'Karşılaştırılacak teklif yok.',
        best: 'En iyi',
        substitute: 'İkame',
        declined: 'Reddedildi',
        noPrice: 'Fiyatsız',
        noAnswer: 'Yanıt yok',
        noteLabel: 'Not',
        bestTotal: 'Satır başına en iyi',
        covered: 'En iyi fiyatı olan kalem',
        bestCount: '{{count}} en iyi',
        otherCurrency: 'Başka para birimi',
        notApproved: 'Tedarikçi onaylı değil',
      },
      source: {
        portal: 'Portal',
        office: 'Ofis',
      },
      revision: 'Sürüm {{no}}',
      award: awardTr,
      revisions: {
        title: 'Sürüm geçmişi · {{supplier}}',
        show: '{{supplier}} sürümleri',
        no: 'Sürüm',
        submitted: 'Gönderim',
        total: 'Toplam Teslim Maliyeti',
        submittedBy: 'Gönderen',
        hash: 'İçerik özeti',
        current: 'Geçerli',
      },
      createPo: 'Sipariş Aç',
      openOrder: '{{no}} aç',
      send: 'Tedarikçilere Gönder',
      sendTitle: 'Tedarikçilere gönder',
      sendBody:
        'Teklif isteğinin davetli tedarikçilere gönderildiği kaydedilir. İsteği kendi e-postanızla gönderin; teklifler burada girilir.',
      invite: 'Tedarikçi Davet Et',
      inviteTitle: 'Tedarikçi davet et',
      inviteBody: 'Yalnız onaylı ve etkin tedarikçi davet edilir.',
      inviteSupplier: 'Tedarikçi',
      inviteConfirm: 'Davet et',
      enterQuotation: 'Teklif Gir',
      editQuotation: 'Teklifi Düzelt',
      quotationOf: '{{supplier}} teklifi',
      select: 'Seç',
      selectOf: '{{supplier}} seç',
      selectTitle: '{{supplier}} seç',
      selectBody: 'Seçilen teklif teklif isteğini kapatır. Sipariş bu tekliften açılır.',
      selectReasonBody:
        'Bu teklif en iyi fiyat değil. Seçim gerekçesini yazın; teklif isteğiyle birlikte saklanır.',
      selectReason: 'Seçim gerekçesi',
      cancel: 'Teklif İsteğini İptal Et',
      cancelTitle: 'Teklif isteğini iptal et',
      cancelBody:
        'Teklif isteği gerekçesiyle saklanır ve değiştirilemez. Talepten yeni teklif isteği açılabilir.',
      requisition: 'Talep',
      vessel: 'Gemi',
      requestDate: 'Talep Tarihi',
      closingDate: 'Kapanış Tarihi',
      currency: 'Para Birimi',
      deliveryLocation: 'Teslim Yeri',
      deliveryRequirement: 'Teslim Koşulu',
      paymentTerms: 'Ödeme Koşulları',
      warranty: 'Garanti',
      validity: 'Geçerlilik',
      technicalSpec: 'Teknik Şartname',
      noQuotations: 'Henüz teklif yok.',
      noQuotationsText: 'Davetli tedarikçilerden gelen teklifleri girin.',
      comparisonEmpty: 'Karşılaştırmak için teklif girin.',
      comparisonNote:
        'En İyi Fiyat, {{currency}} cinsinden eksiksiz teklifler içinde en düşük toplam teslim maliyetidir.',
      selectedNote: '{{supplier}} seçildi: {{reason}}',
      selectedNoReason: '{{supplier}} seçildi (en iyi fiyat).',
      cancelledNote: 'İptal edildi: {{reason}}',
      events: {
        created: 'Teklif isteği açıldı',
        sent: 'Tedarikçilere gönderildi',
        published: 'Tedarikçi portalında yayımlandı',
        quotation: '{{supplier}} teklifi geldi',
        selected: '{{supplier}} seçildi',
        cancelled: 'Teklif isteği iptal edildi',
      },
      noComments: 'Henüz yorum yok.',
      notices: {
        created: '{{no}} teklif isteği açıldı.',
        sent: '{{no}} tedarikçilere gönderildi olarak işaretlendi.',
        published: '{{no}} tedarikçi portalında yayımlandı.',
        invited: '{{supplier}} davet edildi.',
        quoted: '{{supplier}} teklifi kaydedildi.',
        selected: '{{supplier}} seçildi.',
        cancelled: '{{no}} iptal edildi.',
      },
      openRequisition: '{{no}} talebini aç',
      loading: 'Teklif isteği yükleniyor…',
      error: 'Teklif isteği yüklenemedi.',
      back: 'Listeye dön',
    },
    quotation: {
      titleNew: 'Teklif · {{supplier}}',
      titleEdit: 'Teklifi düzelt · {{supplier}}',
      header: 'Teklif',
      ref: 'Teklif No',
      received: 'Geliş Tarihi',
      currency: 'Para Birimi',
      leadTime: 'Teslim Süresi (gün)',
      validity: 'Geçerlilik (gün)',
      warranty: 'Garanti',
      prices: 'Birim Fiyatlar',
      unitPrice: 'Birim Fiyat ({{currency}})',
      priceOf: '{{name}} birim fiyatı',
      lineTotal: 'Toplam',
      costs: 'Maliyetler',
      discount: 'İndirim',
      freight: 'Navlun',
      customs: 'Gümrük',
      tax: 'Vergi',
      serviceFee: 'Hizmet Bedeli',
      remarks: 'Açıklama',
      itemsTotal: 'Kalemler toplamı',
      landedTotal: 'Toplam teslim maliyeti',
      incomplete: 'Teklifin karşılaştırılması için her kalem fiyatlanmalı.',
      save: 'Teklifi Kaydet',
      cancel: 'Vazgeç',
    },
    create: {
      title: 'Teklif İsteği Aç · {{no}}',
      general: 'Teklif İsteği',
      suppliers: 'Tedarikçiler',
      suppliersPlaceholder: 'Tedarikçi seçin',
      suppliersHint: 'Yalnız onaylı ve etkin tedarikçi davet edilir.',
      items: 'Kalemler',
      itemOf: '{{name}} dahil',
      closingDate: 'Kapanış Tarihi',
      currency: 'Para Birimi',
      deliveryLocation: 'Teslim Yeri',
      deliveryRequirement: 'Teslim Koşulu',
      paymentTerms: 'Ödeme Koşulları',
      warranty: 'Garanti',
      validity: 'Geçerlilik (gün)',
      technicalSpec: 'Teknik Şartname',
      create: 'Teklif İsteği Aç',
      cancel: 'Vazgeç',
      loading: 'Tedarikçiler yükleniyor…',
    },
  },
  errors: errorsTr,
  field: fieldTr,
  po: poTr,
  grn: grnTr,
};
